import ReactDOM from "react-dom";
import styles from "./styles.module.scss";
import { FC, Props } from "./typings";
import { useState } from "react";
import Button from "components/Button";
import NotesService from "api/NotesService";
import encryptNoteFields, {
  IEncryptedNoteFields,
} from "utils/encryptNoteFields";
import { CompanyKeyMissingError } from "utils/encryptForCompany";
import { ENotificationType, notification } from "utils/notification";

import CloseIcon from "assets/icons/close.svg?react";
import { INote } from "components/NotesDesk";
import RichEditor from "components/RichEditor";
import Input from "components/Input";
import NewSearchCases from "components/NewSearchCases";
import { useTranslation } from "react-i18next";

const AddNote: FC<Props> = ({
  defaultValues,
  toggle,
  refetch,
  addNote,
  caseId,
  participantId,
  invoiceId,
  withCasePicker,
  slide,
  slideOffsetRight,
}) => {
  const { t } = useTranslation();
  const [content, setContent] = useState(defaultValues?.content ?? "");
  const [title, setTitle] = useState(defaultValues?.title ?? "");
  // Teczka powiązana z notatką: domyślnie z propsa, opcjonalnie wybrana w formatce.
  const [caseIdLocal, setCaseIdLocal] = useState<string | undefined>(caseId);

  const { mutate: addNoteS } = NotesService.useCreateNote();
  const { mutate: editNote } = NotesService.useEditNote();

  const handleCreate = async () => {
    // Treść notatki szyfruje KLUCZ OBIEKTU, a klucz obiektu owija klucz publiczny FIRMY
    // (utils/encryptNoteFields, docs/model-kluczy-firmowych.md — inwariant I1). Wcześniej
    // Title/Content były szyfrowane WPROST kluczem profilu, więc notatki widział wyłącznie
    // ich autor i nie było czego przewijać przy rotacji klucza.
    let payload: IEncryptedNoteFields;
    try {
      payload = await encryptNoteFields(title, content);
    } catch (error) {
      // FAIL-CLOSED — patrz utils/encryptForCompany.ts.
      notification({
        type: ENotificationType.ERROR,
        title: t("errors.occurred"),
        text:
          error instanceof CompanyKeyMissingError
            ? error.message
            : t("errors.somethingWentWrong"),
      });
      return;
    }

    if (defaultValues?.id) {
      editNote(
        {
          Id: defaultValues.id,
          Title: payload.Title,
          Content: payload.Content,
          EncryptionKeyId: payload.EncryptionKeyId,
        },
        {
          onSuccess: () => {
            toggle();
            refetch();
          },
        },
      );
      return;
    }

    addNoteS(
      {
        Content: payload.Content,
        Title: payload.Title,
        EncryptionKeyId: payload.EncryptionKeyId,
        CaseId: caseIdLocal,
        ParticipantId: participantId,
        InvoiceId: invoiceId,
      },
      {
        onSuccess: (response) => {
          const note = response.data as INote;
          note.Title = title;
          note.Content = content;
          note.decoded = true;
          addNote(response.data);
          toggle();
        },
      },
    );
  };

  return ReactDOM.createPortal(
    <>
      <div
        className={`${styles.modal} ${slide ? styles.slideOverlay : ""}`}
        onClick={
          slide ? (e) => e.target === e.currentTarget && toggle() : undefined
        }
      >
        <div
          className={`${styles.window} ${slide ? styles.slideWindow : ""}`}
          style={slide ? { right: slideOffsetRight ?? 0 } : undefined}
        >
          <div className={styles.title}>
            {t("modals.note")}
            <CloseIcon onClick={toggle} className={styles.icon} />
          </div>
          <div className={styles.wrapper}>
            <div className={styles.inputs}>
              {withCasePicker && !defaultValues?.id && (
                <NewSearchCases onChoose={(ce) => setCaseIdLocal(ce.Id)} />
              )}
              <Input
                onChange={(a) => setTitle(a)}
                value={title}
                label={t("forms.subject")}
              />
            </div>
            <div className={styles.editor}>
              <RichEditor setContent={setContent} content={content} />
            </div>
            <div className={styles.button}>
              <Button onClick={() => handleCreate()} text={t("common.save")} />
            </div>
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
};

export default AddNote;
